import { Link } from 'react-router-dom';

import { PageMeta } from '@/components/PageMeta';
import { ROUTES } from '@/config/routes';
import { ArrowRight, Check, FileText, Globe, LayoutTemplate, ListTree, Newspaper, Trophy } from 'lucide-react';

const contactHref = `${ROUTES.CONTACT}?subject=${encodeURIComponent('Question générale')}&message=${encodeURIComponent(
  'Bonjour, je souhaite en savoir plus sur l’offre Site web personnalisable BoulesConnect.',
)}`;

const features = [
  {
    icon: LayoutTemplate,
    title: 'Vos pages, sans complexité',
    description:
      'Créez les pages de présentation de votre association avec un éditeur simple et une mise en page encadrée.',
  },
  {
    icon: ListTree,
    title: 'Une navigation claire',
    description:
      'Organisez vos menus, sous-menus et sous-sous-menus, avec trois niveaux maximum et une navigation adaptée au mobile.',
  },
  {
    icon: FileText,
    title: 'Vos documents au même endroit',
    description: 'Mettez à disposition règlements, convocations, comptes rendus et autres documents PDF.',
  },
  {
    icon: Newspaper,
    title: 'Vos informations aux joueurs',
    description:
      'Présentez vos actualités et propositions aux joueurs sans reconstruire un deuxième système de gestion.',
  },
  {
    icon: Trophy,
    title: 'Vos concours reliés à BoulesConnect',
    description:
      'Ajoutez des liens directs vers les fiches de concours et laissez les inscriptions et paiements sur BoulesConnect.',
  },
  {
    icon: Globe,
    title: 'Un site adapté à votre association',
    description:
      'Un modèle responsive avec personnalisation limitée du logo, de la bannière et des couleurs principales.',
  },
] as const;

const principles = [
  'Le site public reste consultable sans compte ni connexion.',
  'Vous gérez le contenu depuis l’Admin BoulesConnect existant.',
  'Les visiteurs rejoignent BoulesConnect pour consulter un concours, se connecter ou s’inscrire.',
  'Pas de constructeur complexe, de méga-menu ni de deuxième espace membre.',
] as const;

export default function SiteWeb() {
  return (
    <main className='min-h-screen bg-slate-50 font-sans text-slate-900 selection:bg-blue-100'>
      <PageMeta
        title='Site web personnalisable'
        description='Un site vitrine simple pour votre club ou association : pages, menus, documents et liens vers vos concours BoulesConnect. 30 € TTC/mois ou 300 € TTC/an.'
        canonical={ROUTES.FONCTIONNALITES_SITE_WEB}
      />

      <section className='bg-linear-to-br from-slate-950 via-blue-950 to-indigo-900 px-6 pb-20 pt-36 text-white md:pb-28 md:pt-44'>
        <div className='mx-auto max-w-5xl text-center'>
          <span className='mb-7 inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/10 px-4 py-2 text-xs font-bold uppercase tracking-wider text-blue-100'>
            <Globe className='h-4 w-4' />
            Offre Site web personnalisable
          </span>
          <h1 className='mx-auto max-w-4xl text-4xl font-extrabold leading-tight md:text-6xl'>
            Votre site associatif,
            <span className='block bg-linear-to-r from-sky-300 to-indigo-300 bg-clip-text text-transparent'>
              géré depuis BoulesConnect
            </span>
          </h1>
          <p className='mx-auto mt-6 max-w-3xl text-lg leading-relaxed text-blue-100 md:text-xl'>
            Présentez votre association, partagez vos documents et guidez les joueurs vers vos concours, sans multiplier
            les outils ni gérer un site compliqué.
          </p>
          <div className='mt-9 flex flex-col justify-center gap-3 sm:flex-row'>
            <Link
              to={contactHref}
              className='inline-flex items-center justify-center gap-2 rounded-xl bg-white px-6 py-3.5 font-bold text-blue-900 shadow-lg transition hover:-translate-y-0.5 hover:bg-blue-50'
            >
              Demander des informations
              <ArrowRight className='h-4 w-4' />
            </Link>
            <a
              href='#offre'
              className='inline-flex items-center justify-center rounded-xl border border-white/25 px-6 py-3.5 font-bold text-white transition hover:bg-white/10'
            >
              Voir les tarifs
            </a>
          </div>
        </div>
      </section>

      <section className='px-6 py-20 md:py-24'>
        <div className='mx-auto max-w-6xl'>
          <div className='mx-auto mb-12 max-w-3xl text-center'>
            <p className='mb-3 text-xs font-extrabold uppercase tracking-[0.2em] text-blue-700'>Simple et intégré</p>
            <h2 className='text-3xl font-extrabold tracking-tight md:text-4xl'>
              L’essentiel pour gérer votre site, pas une usine à gaz
            </h2>
            <p className='mt-4 text-lg leading-relaxed text-slate-600'>
              Une vitrine facile à administrer, conçue pour les clubs et les organisateurs qui veulent communiquer avec
              leurs joueurs sans gérer un deuxième outil au quotidien.
            </p>
          </div>

          <div className='grid gap-5 md:grid-cols-2 lg:grid-cols-3'>
            {features.map(feature => {
              const Icon = feature.icon;
              return (
                <article key={feature.title} className='rounded-2xl border border-slate-200 bg-white p-6 shadow-sm'>
                  <div className='mb-5 flex h-11 w-11 items-center justify-center rounded-xl bg-blue-50 text-blue-700'>
                    <Icon className='h-5 w-5' />
                  </div>
                  <h3 className='text-lg font-bold text-slate-900'>{feature.title}</h3>
                  <p className='mt-2 text-sm leading-relaxed text-slate-600'>{feature.description}</p>
                </article>
              );
            })}
          </div>
        </div>
      </section>

      <section className='border-y border-slate-200 bg-white px-6 py-16 md:py-20'>
        <div className='mx-auto grid max-w-5xl gap-10 md:grid-cols-2 md:items-center'>
          <div>
            <p className='mb-3 text-xs font-extrabold uppercase tracking-[0.2em] text-blue-700'>
              Pensé pour votre communauté
            </p>
            <h2 className='text-3xl font-extrabold tracking-tight'>Votre site informe. BoulesConnect fait le reste.</h2>
            <p className='mt-4 leading-relaxed text-slate-600'>
              Les pages et documents restent publics. Lorsqu’un visiteur souhaite consulter un concours, se connecter ou
              inscrire une équipe, il rejoint BoulesConnect : pas de compte membre séparé et pas de paiement dupliqué
              sur le site vitrine.
            </p>
          </div>
          <ul className='space-y-3'>
            {principles.map(principle => (
              <li key={principle} className='flex items-start gap-3 rounded-xl bg-slate-50 p-4'>
                <span className='mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-emerald-100 text-emerald-700'>
                  <Check className='h-3.5 w-3.5' />
                </span>
                <span className='text-sm font-medium leading-relaxed text-slate-700'>{principle}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      <section id='offre' className='px-6 py-20 md:py-24'>
        <div className='mx-auto max-w-5xl'>
          <div className='mx-auto mb-10 max-w-2xl text-center'>
            <p className='mb-3 text-xs font-extrabold uppercase tracking-[0.2em] text-blue-700'>
              Une option indépendante
            </p>
            <h2 className='text-3xl font-extrabold tracking-tight md:text-4xl'>
              Un tarif simple, sans changer de forfait
            </h2>
            <p className='mt-4 text-slate-600'>
              L’option Site web est distincte des offres Club Pro et Organisateur Forfait.
            </p>
          </div>

          <div className='grid gap-5 md:grid-cols-2'>
            <article className='rounded-3xl border border-slate-200 bg-white p-7 shadow-sm md:p-9'>
              <p className='text-sm font-bold text-slate-500'>Formule mensuelle</p>
              <p className='mt-3 text-4xl font-extrabold text-slate-950'>
                30 € <span className='text-base font-semibold text-slate-500'>TTC / mois</span>
              </p>
              <p className='mt-3 text-sm leading-relaxed text-slate-600'>Pour avancer sans engagement annuel.</p>
              <Link
                to={contactHref}
                className='mt-7 inline-flex w-full items-center justify-center gap-2 rounded-xl border border-slate-300 px-5 py-3 font-bold text-slate-800 transition hover:bg-slate-50'
              >
                Demander des informations <ArrowRight className='h-4 w-4' />
              </Link>
            </article>

            <article className='relative rounded-3xl border border-blue-300 bg-white p-7 shadow-lg shadow-blue-950/5 md:p-9'>
              <span className='absolute right-6 top-6 rounded-full bg-blue-50 px-3 py-1 text-xs font-extrabold text-blue-700'>
                2 mois offerts
              </span>
              <p className='text-sm font-bold text-slate-500'>Formule annuelle</p>
              <p className='mt-3 text-4xl font-extrabold text-slate-950'>
                300 € <span className='text-base font-semibold text-slate-500'>TTC / an</span>
              </p>
              <p className='mt-3 text-sm leading-relaxed text-slate-600'>
                Soit 25 € TTC par mois en équivalent annuel, avec 60 € TTC d’économie sur douze mensualités.
              </p>
              <Link
                to={contactHref}
                className='mt-7 inline-flex w-full items-center justify-center gap-2 rounded-xl bg-blue-700 px-5 py-3 font-bold text-white transition hover:bg-blue-800'
              >
                Demander des informations <ArrowRight className='h-4 w-4' />
              </Link>
            </article>
          </div>

          <p className='mx-auto mt-6 max-w-3xl text-center text-xs leading-relaxed text-slate-500'>
            Tarifs TTC. L’offre est en préparation : le paiement en ligne et l’activation automatique ne sont pas encore
            disponibles. Contactez-nous pour discuter du pilote et des modalités de lancement.
          </p>
        </div>
      </section>
    </main>
  );
}
